import api from "@/utils/axios";
import { defineStore } from "pinia";

export const useAuthStore = defineStore("auth", {
  state: () => ({
    token: null as string | null,
    user: null as Record<string, any> | null,
    // Always stored as { permKey: 1|0 } so sidebar `permissions?.key == 1` works
    permissions: {} as Record<string, number>,
    // Staff-only: array of individual action codes ["FACTURES", "STOCK", ...]
    staffCodes: [] as string[],
  }),

  actions: {
    setToken(token: string) {
      this.token = token;
    },
    setUser(user: Record<string, any>) {
      this.user = user;
    },
    clearAuth() {
      this.token = null;
      this.user = null;
      this.permissions = {};
      this.staffCodes = [];
    },

    // Accepts either object {key: 1|0} or string[] from login responses
    setPermissions(permissions: Record<string, any> | string[]) {
      if (Array.isArray(permissions)) {
        const obj: Record<string, number> = {};
        permissions.forEach((k) => { obj[k] = 1; });
        this.permissions = obj;
      } else {
        this.permissions = permissions ?? {};
      }
    },

    async fetchPermissions() {
      try {
        if (!this.user) return;

        const res = await api.get(`/api/auth/client/fecthPerms`, {
          headers: { Authorization: `Bearer ${this.token}` },
        });

        // Always refresh the stored user so is_staff stays current
        if (res.data.user) this.user = { ...this.user, ...res.data.user };

        // Detect staff by is_staff flag or customer_id presence. Computed
        // after the merge above so a freshly-set placeholder user (e.g. from
        // a staff-link bootstrap) still resolves correctly on the first call.
        const isStaff = !!this.user?.is_staff || !!this.user?.customer_id;

        if (isStaff) {
          // permissions = customer's feature gates {stock:1, Parcels:1, ...} — controls sidebar visibility
          this.permissions = res.data.user?.perms ?? {};
          // staffCodes = staff's individual action codes ["STOCK", "BON_LIVRAISON", ...] — controls per-section access
          this.staffCodes = Array.isArray(res.data.data) ? res.data.data : [];
        } else {
          // Admin branch: res.data.data = object {stock:1, Parcels:1, ...} directly
          this.permissions = (res.data.data && typeof res.data.data === "object" && !Array.isArray(res.data.data))
            ? res.data.data
            : {};
        }
        console.log(this.permissions)
      } catch (error) {
        console.error("Failed to fetch permissions:", error);
      }
    },

    can(key: string) {
      return this.permissions[key] === 1;
    },
  },

  persist: true,
});
